/**
 * ErrorBoundary — catches unhandled React render errors.
 *
 * Wraps the entire component tree (in `App.tsx`) so that a single
 * component crash doesn't unmount the whole application.  Shows a
 * user-friendly fallback UI with the option to retry or reload.
 *
 * Also logs the error to the console and optionally dispatches an
 * `addNotification` action to the Redux store (best-effort — the
 * store may not be available if the error happens during init).
 */
import React, { Component, ErrorInfo, ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional fallback renderer — receives the error and a retry callback. */
  fallback?: (error: Error, retry: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error("[ErrorBoundary] Uncaught error:", error, errorInfo);

    // Best-effort: push a system notification into the Redux store
    try {
      const { store } = require("@store/index");
      const { addNotification } = require("@store/alert-slice");
      store.dispatch(
        addNotification({
          type: "system",
          title: "Application Error",
          message: error?.message || "An unexpected error occurred.",
        })
      );
    } catch {
      // Store not available yet — ignore
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { hasError, error } = this.state;

    if (hasError && error) {
      // Custom fallback renderer
      if (this.props.fallback) {
        return this.props.fallback(error, this.handleRetry);
      }

      // Default fallback UI
      return (
        <div className="error-boundary-fallback">
          <div className="error-boundary-card">
            <div className="error-boundary-icon">⚠</div>
            <h2 className="error-boundary-title">Something went wrong</h2>
            <p className="error-boundary-message">
              {error.message || "An unexpected error occurred."}
            </p>
            <div className="error-boundary-actions">
              <button
                className="error-boundary-btn error-boundary-btn--retry"
                onClick={this.handleRetry}
              >
                Try Again
              </button>
              <button
                className="error-boundary-btn error-boundary-btn--reload"
                onClick={this.handleReload}
              >
                Reload App
              </button>
            </div>
            {process.env.NODE_ENV === "development" && this.state.errorInfo && (
              <details className="error-boundary-details">
                <summary>Error Details</summary>
                <pre>{error.stack}</pre>
                <pre>{this.state.errorInfo.componentStack}</pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
